
// src/components/admin/page-management/section-forms/ImageForm.js
import React, { useState, useEffect } from 'react';
import ImageUploader from '../ImageUploader';

const ImageForm = ({ content, updateContent, pageSlug }) => {
  const [previewMode, setPreviewMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Effects
  const handleImageUpload = (imagePath) => {
    console.log('ImageForm handling image upload with path:', imagePath);
    updateContent('imageUrl', imagePath);
  };

  // Generate image preview URL
  const imagePreviewUrl = content?.imageUrl || '';
  
  // Handle image overlay
  const handleAddOverlay = () => {
    if (!content.overlay) {
      updateContent('overlay', {
        enabled: true,
        color: 'rgba(0,0,0,0.4)',
        blendMode: 'normal'
      });
    } else {
      updateContent('overlay', {
        ...content.overlay,
        enabled: !content.overlay.enabled
      });
    }
  };
  
  // Preset options for aspect ratio
  const aspectRatioOptions = [
    { label: 'Original', value: 'auto' },
    { label: 'Square (1:1)', value: '1/1' },
    { label: 'Landscape (16:9)', value: '16/9' },
    { label: 'Landscape (4:3)', value: '4/3' },
    { label: 'Portrait (3:4)', value: '3/4' },
    { label: 'Portrait (9:16)', value: '9/16' },
    { label: 'Custom', value: 'custom' }
  ];
  
  // Image effects options
  const effectOptions = [
    { label: 'None', value: 'none' },
    { label: 'Grayscale', value: 'grayscale(100%)' },
    { label: 'Sepia', value: 'sepia(100%)' },
    { label: 'Blur', value: 'blur(3px)' },
    { label: 'Brighten', value: 'brightness(120%)' },
    { label: 'Contrast', value: 'contrast(150%)' },
    { label: 'Warm', value: 'sepia(30%)' },
    { label: 'Cool', value: 'hue-rotate(30deg)' }
  ];
  
  // Animation options
  const animationOptions = [
    { label: 'None', value: 'none' },
    { label: 'Fade In', value: 'fadeIn' },
    { label: 'Slide Up', value: 'slideUp' },
    { label: 'Slide In From Left', value: 'slideInLeft' },
    { label: 'Slide In From Right', value: 'slideInRight' },
    { label: 'Zoom In', value: 'zoomIn' },
    { label: 'Pulse', value: 'pulse' }
  ];
  
  // Border style options
  const borderStyleOptions = [
    { label: 'None', value: 'none' },
    { label: 'Solid', value: 'solid' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Dotted', value: 'dotted' },
    { label: 'Double', value: 'double' }
  ];

  // Interactive border radius slider
  const BorderRadiusSelector = () => {
    const currentRadius = content.borderRadius || '0';
    
    const presetValues = [
      { label: 'None', value: '0' },
      { label: 'Slight', value: '4px' },
      { label: 'Medium', value: '8px' },
      { label: 'Large', value: '16px' },
      { label: 'Round', value: '9999px' }
    ];
    
    return (
      <div className="mt-2">
        <div className="flex flex-wrap gap-2">
          {presetValues.map(preset => (
            <button
              key={preset.value}
              type="button"
              className={`px-3 py-1 text-xs border rounded hover:bg-gray-100 
              ${currentRadius === preset.value ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}
              onClick={() => updateContent('borderRadius', preset.value)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Image Section</h3>
      
      {/* Image Source Input */}
      <div className="mb-6 bg-white p-4 border rounded-md shadow-sm">
        <h4 className="font-medium text-gray-700 mb-3">Image Source</h4>
        
        <div className="mb-4 flex gap-1">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1 text-gray-700">Image URL</label>
            <input
              type="text"
              className="w-full p-2 border rounded bg-white"
              value={content?.imageUrl || ''}
              onChange={(e) => updateContent('imageUrl', e.target.value)}
              placeholder="Enter image URL or upload below"
            />
          </div>
          
          <div className="ml-1">
            <label className="block text-sm font-medium mb-1 text-gray-700">Link (Optional)</label>
            <input
              type="text"
              className="w-full p-2 border rounded bg-white"
              value={content?.linkUrl || ''}
              onChange={(e) => updateContent('linkUrl', e.target.value)}
              placeholder="URL when image is clicked"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Upload Image</label>
          <ImageUploader 
            onImageUpload={handleImageUpload} 
            pageSlug={pageSlug}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Alt Text</label>
          <input
            type="text"
            className="w-full p-2 border rounded bg-white"
            value={content?.imageAlt || ''}
            onChange={(e) => updateContent('imageAlt', e.target.value)}
            placeholder="Describe the image for accessibility"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Caption (Optional)</label>
          <input
            type="text"
            className="w-full p-2 border rounded bg-white"
            value={content?.caption || ''}
            onChange={(e) => updateContent('caption', e.target.value)}
            placeholder="Caption displayed below the image"
          />
        </div>
      </div>
      
      {/* Layout & Sizing */}
      <div className="mb-6 bg-white p-4 border rounded-md shadow-sm">
        <h4 className="font-medium text-gray-700 mb-3">Layout & Sizing</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Alignment</label>
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.alignment === 'left' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('alignment', 'left')}
              >
                Left
              </button>
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.alignment === 'center' || !content?.alignment ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('alignment', 'center')}
              >
                Center
              </button>
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.alignment === 'right' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('alignment', 'right')}
              >
                Right
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Size</label>
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.size === 'small' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('size', 'small')}
              >
                Small
              </button>
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.size === 'medium' || !content?.size ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('size', 'medium')}
              >
                Medium
              </button>
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.size === 'large' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('size', 'large')}
              >
                Large
              </button>
              <button
                type="button"
                className={`flex-1 p-2 border rounded ${content?.size === 'full' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
                onClick={() => updateContent('size', 'full')}
              >
                Full
              </button>
            </div>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Width</label>
            <div className="flex">
              <input
                type="text"
                className="w-full p-2 border rounded-l bg-white"
                value={content?.width || '100%'}
                onChange={(e) => updateContent('width', e.target.value)}
                placeholder="100%, 500px, auto"
              />
              <select
                className="border-l-0 border p-2 rounded-r bg-white"
                value={content?.width?.includes('%') ? '%' : content?.width?.includes('px') ? 'px' : 'auto'}
                onChange={(e) => {
                  let value = content?.width || '100';
                  if (value.includes('%') || value.includes('px')) {
                    value = parseInt(value) || 100;
                  }
                  
                  if (e.target.value === 'auto') {
                    updateContent('width', 'auto');
                  } else {
                    updateContent('width', `${value}${e.target.value}`);
                  }
                }}
              >
                <option value="%">%</option>
                <option value="px">px</option>
                <option value="auto">auto</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Max Height</label>
            <div className="flex">
              <input
                type="text"
                className="w-full p-2 border rounded-l bg-white"
                value={content?.maxHeight?.replace('px', '') || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    updateContent('maxHeight', 'none');
                  } else {
                    updateContent('maxHeight', `${value}px`);
                  }
                }}
                placeholder="Leave empty for no limit"
              />
              <div className="border-l-0 border p-2 rounded-r bg-gray-100">
                px
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Aspect Ratio</label>
          <select
            className="w-full p-2 border rounded bg-white"
            value={content?.aspectRatio || 'auto'}
            onChange={(e) => updateContent('aspectRatio', e.target.value)}
          >
            {aspectRatioOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {content?.aspectRatio === 'custom' && (
            <div className="mt-2 flex gap-2 items-center">
              <input
                type="text"
                className="w-20 p-2 border rounded bg-white"
                value={content?.aspectRatioWidth || '16'}
                onChange={(e) => updateContent('aspectRatioWidth', e.target.value)}
              />
              <span>:</span>
              <input
                type="text"
                className="w-20 p-2 border rounded bg-white"
                value={content?.aspectRatioHeight || '9'}
                onChange={(e) => updateContent('aspectRatioHeight', e.target.value)}
              />
            </div>
          )}
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Object Fit</label>
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.objectFit === 'cover' || !content?.objectFit ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('objectFit', 'cover')}
            >
              Cover
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.objectFit === 'contain' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('objectFit', 'contain')}
            >
              Contain
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.objectFit === 'fill' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('objectFit', 'fill')}
            >
              Fill
            </button>
          </div>
        </div>
      </div>
      
      {/* Styling */}
      <div className="mb-6 bg-white p-4 border rounded-md shadow-sm">
        <div className="flex justify-between items-center">
          <h4 className="font-medium text-gray-700">Styling & Effects</h4>
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
          </button>
        </div>
        
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1 text-gray-700">Border Radius</label>
          <BorderRadiusSelector />
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Shadow</label>
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${!content?.shadow ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('shadow', '')}
            >
              None
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.shadow === 'shadow-sm' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('shadow', 'shadow-sm')}
            >
              Small
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.shadow === 'shadow' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('shadow', 'shadow')}
            >
              Medium
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.shadow === 'shadow-lg' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('shadow', 'shadow-lg')}
            >
              Large
            </button>
          </div>
        </div>
        
        {showAdvanced && (
          <>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Border</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <select
                    className="w-full p-2 border rounded bg-white"
                    value={content?.borderStyle || 'none'}
                    onChange={(e) => updateContent('borderStyle', e.target.value)}
                  >
                    {borderStyleOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded bg-white"
                    value={content?.borderWidth || '1px'}
                    onChange={(e) => updateContent('borderWidth', e.target.value)}
                    placeholder="1px"
                    disabled={content?.borderStyle === 'none'}
                  />
                </div>
              </div>
              
              {content?.borderStyle !== 'none' && (
                <div className="mt-2">
                  <input
                    type="text"
                    className="w-full p-2 border rounded bg-white"
                    value={content?.borderColor || '#000000'}
                    onChange={(e) => updateContent('borderColor', e.target.value)}
                    placeholder="Border color (hex or name)"
                  />
                </div>
              )}
            </div>
            
            <div className="mt-4">
              <label className="flex items-center mb-2">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={content?.overlay?.enabled || false}
                  onChange={handleAddOverlay}
                />
                <span className="text-sm font-medium text-gray-700">Add Overlay</span>
              </label>
              
              {content?.overlay?.enabled && (
                <>
                  <input
                    type="text"
                    className="w-full p-2 border rounded bg-white mb-2"
                    value={content?.overlay?.color || 'rgba(0,0,0,0.4)'}
                    onChange={(e) => updateContent('overlay', {
                      ...content.overlay,
                      color: e.target.value
                    })}
                    placeholder="rgba(0,0,0,0.4)"
                  />
                  
                  <select
                    className="w-full p-2 border rounded bg-white"
                    value={content?.overlay?.blendMode || 'normal'}
                    onChange={(e) => updateContent('overlay', {
                      ...content.overlay,
                      blendMode: e.target.value
                    })}
                  >
                    <option value="normal">Normal</option>
                    <option value="multiply">Multiply</option>
                    <option value="screen">Screen</option>
                    <option value="overlay">Overlay</option>
                    <option value="darken">Darken</option>
                    <option value="lighten">Lighten</option>
                    <option value="color-dodge">Color Dodge</option>
                    <option value="color-burn">Color Burn</option>
                    <option value="hard-light">Hard Light</option>
                    <option value="soft-light">Soft Light</option>
                    <option value="difference">Difference</option>
                    <option value="exclusion">Exclusion</option>
                  </select>
                </>
              )}
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Visual Effects</label>
              <select
                className="w-full p-2 border rounded bg-white"
                value={content?.effect || 'none'}
                onChange={(e) => updateContent('effect', e.target.value)}
              >
                {effectOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Animation</label>
              <select
                className="w-full p-2 border rounded bg-white"
                value={content?.animation || 'none'}
                onChange={(e) => updateContent('animation', e.target.value)}
              >
                {animationOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
      
      {/* Image Preview */}
      {content?.imageUrl && (
        <div className="mt-6 border p-4 rounded-md">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-gray-700">Preview</h4>
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? 'Compact View' : 'Expanded View'}
            </button>
          </div>
          
          <div 
            className={`relative overflow-hidden bg-gray-100 p-2 ${previewMode ? 'h-[400px]' : 'h-60'}`}
            style={{
              textAlign: content?.alignment || 'center',
              padding: '16px'
            }}
          >
            <div
              className={`relative inline-block ${content?.size === 'small' ? 'max-w-xs' : content?.size === 'large' ? 'max-w-2xl' : content?.size === 'full' ? 'w-full' : 'max-w-lg'}`}
              style={{
                width: content?.width || '100%',
                maxHeight: content?.maxHeight || 'none',
                aspectRatio: content?.aspectRatio && content.aspectRatio !== 'auto' ? content.aspectRatio.replace('/', '/') : 'auto',
                boxShadow: content?.shadow === 'shadow-sm' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 
                          content?.shadow === 'shadow' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' :
                          content?.shadow === 'shadow-lg' ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
                borderRadius: content?.borderRadius || '0',
                overflow: 'hidden',
                border: content?.borderStyle && content.borderStyle !== 'none' ? 
                        `${content.borderWidth || '1px'} ${content.borderStyle} ${content.borderColor || '#000'}` : 'none'
              }}
            >
              <img 
                src={imagePreviewUrl}
                alt={content?.imageAlt || 'Preview'} 
                className="w-full h-full"
                style={{
                  objectFit: content?.objectFit || 'cover',
                  filter: content?.effect !== 'none' ? content.effect : 'none',
                  animation: content?.animation !== 'none' && content.animation ? `${content.animation} 1s` : 'none',
                }}
              />
              
              {content?.overlay?.enabled && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundColor: content.overlay.color || 'rgba(0,0,0,0.4)',
                    mixBlendMode: content.overlay.blendMode || 'normal'
                  }}
                ></div>
              )}
            </div>
            
            {content?.caption && (
              <div className="mt-2 text-center text-gray-600 text-sm">
                {content.caption}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageForm;
